/* ========== Model ========== */
//Blake Gilmore 4/25 AI used to figure out lit-html
/**
 * Manages the bookmark data and its persistence in localStorage.
 * Acts as the single source of truth for the bookmark list, ensuring
 * data is validated before being saved or used by the rest of the app.
 */
export class BookmarkModel {
  /**
   * Restores the bookmark list from localStorage on startup so the user's
   * bookmarks survive page refreshes and browser restarts. Falls back to
   * a set of sample bookmarks when storage is empty or contains corrupt data.
   */
  constructor() {
    try {
      const savedBookmarks = JSON.parse(localStorage.getItem("bookmarks")); // array to hold bookmarks
      // DONE!!: Add a line of code that retrieves bookmarks from local storage into savedBookmarks
      if (!Array.isArray(savedBookmarks) || !this.allValid(savedBookmarks)) {
        throw new Error('Invalid bookmark payload');
      }
      this.bookmarks = savedBookmarks;
    } catch (e) {
      // Provide starter entries if local storage is empty/corrupt.
      this.bookmarks = [
        {
          description: 'Really cool site for open source photos',
          image: '',
          link: 'https://www.pexels.com/',
          title: 'https://www.pexels.com/'
        },
        {
          description: "Brian's professional website",
          image: '',
          link: 'https://profbird.dev/',
          title: 'https://profbird.dev/'
        }
      ];
    }
  }

  /**
   * Guards against corrupt or maliciously crafted localStorage data by
   * confirming that a bookmark object has all required string properties
   * before it is accepted into the model.
   */
  isValidBookmark(bookmark) {
    return (
      typeof bookmark === 'object' &&
      bookmark !== null &&
      typeof bookmark.description === 'string' &&
      typeof bookmark.link === 'string' &&
      typeof bookmark.title === 'string' &&
      typeof bookmark.image === 'string'
    );
  }

  /**
   * Validates every entry in an array so the model can reject an entire
   * saved list the moment any single bookmark fails the shape check.
   * Check every bookmark in the array using a for loop.
   */
  allValid(bookmarks) {
    for (let i = 0; i < bookmarks.length; i++) {
      if (!this.isValidBookmark(bookmarks[i])) {
        return false;
      }
    }
    return true;
  }

  /**
   * Saves a new bookmark list as the authoritative state, persists it to
   * localStorage so it survives page reloads, and immediately re-renders the view.
   */
  commit(bookmarks) {
    // DONE: write this method
    this.bookmarks = bookmarks;
    localStorage.setItem("bookmarks", JSON.stringify(bookmarks));
    this.onBookmarkListChanged(bookmarks);
  }

  /**
   * Registers the controller's handler so the model can push updates
   * outward without depending directly on the view or controller classes,
   * keeping the MVC layers loosely coupled.
   */
  subscribeBookmarkListChanged(callback) {
    this.onBookmarkListChanged = callback;
  }

  /**
   * Rejects non-HTTP(S) URLs (such as javascript: or data: schemes) before
   * they can be stored or rendered as links, preventing XSS attacks via
   * malicious bookmark URLs.
   */
  isSafeHttpUrl(value) {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  }

  /**
   * Validates the URL for safety, then appends a new bookmark to the list
   * so the user's addition is immediately reflected in storage and the UI.
   * Returns false when the URL is unsafe so the view can show an error.
   */
  async addBookmark(url, description, previewData) {
    if (!this.isSafeHttpUrl(url)) {
      return false;
    }

    const newBookmark = {
      description: description,
      image: `${previewData.image}`,
      link: url,
      title: `${previewData.title}`
    };

    // Build a new array with the added bookmark.
    
    // DONE: add the new bookmark to the array of bookmarks and put it in local storage
   this.commit([...this.bookmarks,newBookmark]);
    return true;
  }

  /**
   * Removes the bookmark at the given position by rebuilding the array
   * without that entry, then commits the change so storage and the UI
   * stay in sync.
   */
  deleteBookmark(index) {
    // Build a new array without the bookmark at the given index.
  
    // DONE: Remove the bookmark from the array and update local storage.
    this.commit(this.bookmarks.filter((_, bookmarkIndex) => bookmarkIndex !== index));
  }
}
